import axios from 'axios'
import AuthService from './auth-header'
import { buildProjectPayload, projectId, normalizeMembers, reconcileProjectMembers, mergeMembersUnion } from '@/utils/project-config'
import {
  getLocalProjects,
  getLocalProject,
  saveLocalProject,
  applyAddProjectMember,
  applyUpdateProjectMember,
  applyRemoveProjectMember,
  applyAssignResponsibility,
  applyAddResponsibility,
  addLocalProjectMember,
  updateLocalProjectMember,
  removeLocalProjectMember,
  assignLocalResponsibilityToMembers,
  addLocalResponsibility,
} from '@/utils/project-store'

/**
 * Project member API shape (POST /members, PUT /members/:empId):
 * {
 *   empId, name, email?, role,
 *   responsibilityTitle?: string,
 *   responsibilityDescription?: string,
 *   attachments?: [{ id, name, url?, filePath?, size?, uploadedAt, uploadedBy }],
 *   addedAt?, addedBy?
 * }
 * Backend should accept multipart for new files; local mode stores base64 data URLs in `url`.
 *
 * Persistence strategy:
 * 1. Prefer member/responsibility sub-routes when available.
 * 2. On 404/501 for those routes, PUT full project (members + responsibilities)
 *    via `/api/pm/projects/:id` — matches legacy DB `members` JSONB.
 * 3. Only fall back to localStorage when the projects API itself is unavailable;
 *    responses then include `source: 'local'` so the UI can warn.
 */

let API_URL = ''
if (process.env.NODE_ENV === "production") {
  API_URL = 'https://ats.sattvahuman.in/api/'
} else {
  API_URL = 'http://192.168.0.30:5000/api/'
}

const PM_PROJECTS_URL = `${API_URL}pm/projects`

/** null = not probed; false = known 404/501 — use localStorage for session. */
let apiSupported = null

/** null = not probed; false = member/responsibility sub-routes missing — use PUT. */
let memberRoutesSupported = null

/** One soft warning per session when /pm/projects is unavailable. */
let projectsApiSoftWarned = false

function isRouteNotImplementedError(err) {
  const statusCode = err?.response?.status
  return statusCode === 404 || statusCode === 501
}

function apiError(err, fallbackMessage = 'Project service unavailable.') {
  if (err?.response?.data && typeof err.response.data === 'object') {
    return {
      status: err.response.data.status || 'ERROR',
      message: err.response.data.message || fallbackMessage,
      data: [],
    }
  }
  return { status: 'ERROR', message: fallbackMessage, data: [] }
}

function resolveBaseProject(projectIdValue, baseProject) {
  if (baseProject && String(projectId(baseProject)) === String(projectIdValue)) {
    return baseProject
  }
  return getLocalProject(projectIdValue)
}

/** Payload shaped like create/update — members carry responsibility* fields (legacy JSONB). */
function putPayloadFromProject(updated) {
  const id = projectId(updated)
  const payload = {
    id,
    name: updated.name,
    description: updated.description || '',
    status: updated.status,
    color: updated.color,
    createdBy: updated.createdBy ?? updated.created_by ?? null,
    createdByName: updated.createdByName ?? updated.created_by_name ?? null,
    members: normalizeMembers(updated.members),
    updatedAt: updated.updatedAt || new Date().toISOString(),
  }
  // Forward-compat: persist numbered steps when present (legacy DB may ignore).
  if (Array.isArray(updated.responsibilities)) {
    payload.responsibilities = updated.responsibilities
  }
  return payload
}

/**
 * Prefer API project body; if it omits the mutation (e.g. PUT ignores members),
 * keep the computed members/responsibilities so the UI stays consistent.
 */
function mergePutResult(apiRow, computed) {
  const fromApi = apiRow ? {
    ...(apiRow.data && !apiRow.name ? apiRow.data : apiRow),
  } : null
  if (!fromApi) return computed
  const apiMembers = normalizeMembers(fromApi.members)
  const computedMembers = normalizeMembers(computed.members)
  const mergedMembers = mergeMembersUnion(apiMembers, computedMembers)
  const preMerged = {
    ...computed,
    ...fromApi,
    id: projectId(fromApi) ?? projectId(computed),
    createdBy: fromApi.createdBy ?? fromApi.created_by ?? computed.createdBy ?? computed.created_by ?? null,
    createdByName: fromApi.createdByName ?? fromApi.created_by_name
      ?? computed.createdByName ?? computed.created_by_name ?? null,
    members: mergedMembers,
    responsibilities: Array.isArray(fromApi.responsibilities) && fromApi.responsibilities.length
      ? fromApi.responsibilities
      : computed.responsibilities,
  }
  const merged = reconcileProjectMembers(preMerged)
  return merged
}

class ProjectService {
  async getProjects(options = {}) {
    if (apiSupported === false) {
      return { status: 'OK', data: getLocalProjects(), source: 'local' }
    }
    try {
      const params = new URLSearchParams()
      if (options.empId) params.set('empId', options.empId)
      const query = params.toString()
      const url = query ? `${PM_PROJECTS_URL}?${query}` : PM_PROJECTS_URL
      const res = await axios.get(url, { headers: AuthService.authHeader() })
      apiSupported = true
      return res.data
    } catch (err) {
      if (isRouteNotImplementedError(err)) {
        apiSupported = false
        return { status: 'OK', data: getLocalProjects(), source: 'local' }
      }
      if (!projectsApiSoftWarned) {
        projectsApiSoftWarned = true
        console.warn('[project] getProjects failed, using local store:', err?.response?.status || err?.message)
      }
      return { status: 'OK', data: getLocalProjects(), source: 'local' }
    }
  }

  async createProject(data) {
    if (apiSupported === false) {
      const saved = saveLocalProject(data)
      return { status: 'OK', data: saved, source: 'local' }
    }
    try {
      const res = await axios.post(PM_PROJECTS_URL, data, { headers: AuthService.authHeader() })
      apiSupported = true
      return res.data
    } catch (err) {
      if (isRouteNotImplementedError(err)) {
        apiSupported = false
        const saved = saveLocalProject(data)
        return { status: 'OK', data: saved, source: 'local' }
      }
      return apiError(err, 'Failed to create project.')
    }
  }

  async updateProject(id, data) {
    if (apiSupported === false) {
      const saved = saveLocalProject({ ...data, id })
      return { status: 'OK', data: saved, source: 'local' }
    }
    try {
      const res = await axios.put(`${PM_PROJECTS_URL}/${id}`, data, { headers: AuthService.authHeader() })
      apiSupported = true
      return res.data
    } catch (err) {
      if (isRouteNotImplementedError(err)) {
        apiSupported = false
        const saved = saveLocalProject({ ...data, id })
        return { status: 'OK', data: saved, source: 'local' }
      }
      return apiError(err, 'Failed to update project.')
    }
  }

  /**
   * Persist a computed project when member sub-routes are unavailable.
   * Uses PUT /pm/projects/:id so legacy `members` JSONB is updated.
   */
  async persistViaProjectPut(projectIdValue, computed, fallbackMessage) {
    if (!computed) {
      return { status: 'ERROR', message: 'Project not found.' }
    }
    const payload = putPayloadFromProject(computed)
    const putRes = await this.updateProject(projectIdValue, payload)
    if (putRes?.status === 'ERROR') {
      return putRes.message
        ? putRes
        : { status: 'ERROR', message: fallbackMessage }
    }
    if (putRes?.source === 'local') {
      return {
        status: 'OK',
        data: mergePutResult(putRes.data, computed),
        source: 'local',
      }
    }
    const merged = mergePutResult(putRes?.data ?? putRes, computed)
    // Keep localStorage mirror in sync so soft-fail reloads don't drop the member.
    try { saveLocalProject(merged) } catch (e) { /* non-blocking */ }
    return {
      status: 'OK',
      data: merged,
      source: 'api-put',
    }
  }

  async localOnlyResult(updated, notFoundMessage = 'Project not found.') {
    if (!updated) return { status: 'ERROR', message: notFoundMessage }
    return { status: 'OK', data: updated, source: 'local' }
  }

  async addMember(projectIdValue, member, addedBy, baseProject = null) {
    if (apiSupported === false) {
      const base = resolveBaseProject(projectIdValue, baseProject)
      const updated = base
        ? (() => {
          const applied = applyAddProjectMember(base, member, addedBy)
          return applied ? saveLocalProject(applied) : null
        })()
        : addLocalProjectMember(projectIdValue, member, addedBy)
      return this.localOnlyResult(updated)
    }

    if (memberRoutesSupported !== false) {
      try {
        const res = await axios.post(
          `${PM_PROJECTS_URL}/${projectIdValue}/members`,
          member,
          { headers: AuthService.authHeader() },
        )
        apiSupported = true
        memberRoutesSupported = true
        return res.data
      } catch (err) {
        if (!isRouteNotImplementedError(err)) {
          return apiError(err, 'Failed to add team member.')
        }
        memberRoutesSupported = false
      }
    }

    const base = resolveBaseProject(projectIdValue, baseProject)
    const computed = applyAddProjectMember(base, member, addedBy)
    return this.persistViaProjectPut(projectIdValue, computed, 'Failed to add team member.')
  }

  async updateMember(projectIdValue, member, baseProject = null) {
    if (apiSupported === false) {
      const base = resolveBaseProject(projectIdValue, baseProject)
      const updated = base
        ? (() => {
          const applied = applyUpdateProjectMember(base, member)
          return applied ? saveLocalProject(applied) : null
        })()
        : updateLocalProjectMember(projectIdValue, member)
      return this.localOnlyResult(updated, 'Project or member not found.')
    }

    if (memberRoutesSupported !== false) {
      try {
        const empId = member.empId ?? member.emp_id
        const res = await axios.put(
          `${PM_PROJECTS_URL}/${projectIdValue}/members/${empId}`,
          member,
          { headers: AuthService.authHeader() },
        )
        apiSupported = true
        memberRoutesSupported = true
        return res.data
      } catch (err) {
        if (!isRouteNotImplementedError(err)) {
          return apiError(err, 'Failed to update team member.')
        }
        memberRoutesSupported = false
      }
    }

    const base = resolveBaseProject(projectIdValue, baseProject)
    const computed = applyUpdateProjectMember(base, member)
    return this.persistViaProjectPut(projectIdValue, computed, 'Failed to update team member.')
  }

  async addResponsibilityToMembers(projectIdValue, targetEmpIds, responsibility, baseProject = null) {
    if (apiSupported === false) {
      const base = resolveBaseProject(projectIdValue, baseProject)
      const updated = base
        ? (() => {
          const applied = applyAddResponsibility(base, targetEmpIds, responsibility)
          return applied ? saveLocalProject(applied) : null
        })()
        : addLocalResponsibility(projectIdValue, targetEmpIds, responsibility)
      return this.localOnlyResult(updated)
    }

    if (memberRoutesSupported !== false) {
      try {
        const res = await axios.post(
          `${PM_PROJECTS_URL}/${projectIdValue}/responsibilities`,
          { targetEmpIds, ...responsibility },
          { headers: AuthService.authHeader() },
        )
        apiSupported = true
        memberRoutesSupported = true
        const base = resolveBaseProject(projectIdValue, baseProject)
        const computed = applyAddResponsibility(base, targetEmpIds, responsibility)
        const merged = reconcileProjectMembers(mergePutResult(res.data, computed))
        const apiMembers = normalizeMembers(this.extractProject(res.data)?.members)
        const mergedMembers = normalizeMembers(merged.members)
        const membersIncomplete = mergedMembers.some(
          m => !apiMembers.some(am => String(am.empId) === String(m.empId)),
        )
        if (membersIncomplete) {
          return this.persistViaProjectPut(
            projectIdValue,
            merged,
            'Failed to sync team members for responsibility.',
          )
        }
        try { saveLocalProject(merged) } catch (e) { /* non-blocking */ }
        return { status: 'OK', data: merged, source: 'api-post' }
      } catch (err) {
        if (!isRouteNotImplementedError(err)) {
          return apiError(err, 'Failed to add responsibility.')
        }
        memberRoutesSupported = false
      }
    }

    const base = resolveBaseProject(projectIdValue, baseProject)
    const computed = applyAddResponsibility(base, targetEmpIds, responsibility)
    return this.persistViaProjectPut(projectIdValue, computed, 'Failed to add responsibility.')
  }

  async assignResponsibilityToMembers(
    projectIdValue,
    sourceEmpId,
    targetEmpIds,
    responsibilityOverride = null,
    baseProject = null,
  ) {
    if (apiSupported === false) {
      const base = resolveBaseProject(projectIdValue, baseProject)
      const updated = base
        ? (() => {
          const applied = applyAssignResponsibility(
            base,
            sourceEmpId,
            targetEmpIds,
            responsibilityOverride,
          )
          return applied ? saveLocalProject(applied) : null
        })()
        : assignLocalResponsibilityToMembers(
          projectIdValue,
          sourceEmpId,
          targetEmpIds,
          responsibilityOverride,
        )
      return this.localOnlyResult(updated, 'Project or source member not found.')
    }

    if (memberRoutesSupported !== false) {
      try {
        const res = await axios.post(
          `${PM_PROJECTS_URL}/${projectIdValue}/members/${sourceEmpId}/assign-responsibility`,
          { targetEmpIds, responsibility: responsibilityOverride },
          { headers: AuthService.authHeader() },
        )
        apiSupported = true
        memberRoutesSupported = true
        return res.data
      } catch (err) {
        if (!isRouteNotImplementedError(err)) {
          return apiError(err, 'Failed to assign responsibility.')
        }
        memberRoutesSupported = false
      }
    }

    const base = resolveBaseProject(projectIdValue, baseProject)
    const computed = applyAssignResponsibility(
      base,
      sourceEmpId,
      targetEmpIds,
      responsibilityOverride,
    )
    return this.persistViaProjectPut(projectIdValue, computed, 'Failed to assign responsibility.')
  }

  async removeMember(projectIdValue, empId, baseProject = null) {
    if (apiSupported === false) {
      const base = resolveBaseProject(projectIdValue, baseProject)
      const updated = base
        ? (() => {
          const applied = applyRemoveProjectMember(base, empId)
          return applied ? saveLocalProject(applied) : null
        })()
        : removeLocalProjectMember(projectIdValue, empId)
      return this.localOnlyResult(updated)
    }

    if (memberRoutesSupported !== false) {
      try {
        const res = await axios.delete(
          `${PM_PROJECTS_URL}/${projectIdValue}/members/${empId}`,
          { headers: AuthService.authHeader() },
        )
        apiSupported = true
        memberRoutesSupported = true
        return res.data
      } catch (err) {
        if (!isRouteNotImplementedError(err)) {
          return apiError(err, 'Failed to remove team member.')
        }
        memberRoutesSupported = false
      }
    }

    const base = resolveBaseProject(projectIdValue, baseProject)
    const computed = applyRemoveProjectMember(base, empId)
    return this.persistViaProjectPut(projectIdValue, computed, 'Failed to remove team member.')
  }

  buildPayload(form, userData) {
    return buildProjectPayload(form, userData)
  }

  extractProject(row) {
    if (!row) return null
    if (row.data && !row.name) return row.data
    return row
  }

  normalizeProject(row) {
    const p = this.extractProject(row)
    if (!p) return null
    return reconcileProjectMembers({
      ...p,
      id: projectId(p),
      members: normalizeMembers(p.members),
      ...(Array.isArray(p.responsibilities) ? { responsibilities: p.responsibilities } : {}),
    })
  }

  /** Merge API body with client-computed project (create/PUT fallbacks). */
  mergeWithComputed(apiRow, computed) {
    return mergePutResult(apiRow, computed)
  }
}

export default new ProjectService()
